import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { FieldChangeEvent, RadioCardField, Text } from '@frontend/design-system';
import { NthDayDropdown, NthWeekStringDropdown, WeekdayDropdown } from '../molecules/recurrence-dropdowns';
import { GetMonthlyRecurrenceFormFieldProps } from '../types';
import { AvailabilityFormRow } from './availability-form-row';
import { RadioCardFieldRow } from './radio-card-field-row';

type Props = {
  getFieldProps: GetMonthlyRecurrenceFormFieldProps;
};

const isChangeEvent = (
  event: FieldChangeEvent | undefined
): event is FieldChangeEvent & { target: { name: string } } => {
  return !!event && 'target' in event && 'name' in event.target;
};

export const MonthlyRecurrenceRow = ({ getFieldProps }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const monthlyFrequencyTypeProps = getFieldProps('monthlyFrequencyType');
  const monthlyFrequencyType = monthlyFrequencyTypeProps.value;

  // The onChange handler for the monthly frequency type (radio) needs to check the event type
  // to avoid issues with the Dropdown fields within the radio option
  const handleChange = (e?: FieldChangeEvent) => {
    if (isChangeEvent(e)) {
      if (e?.target?.name !== monthlyFrequencyTypeProps.name) return;
    }
    monthlyFrequencyTypeProps.onChange?.(e);
  };

  return (
    <AvailabilityFormRow>
      <RadioCardFieldRow {...monthlyFrequencyTypeProps} label={''} onChange={handleChange} hasNonFloatingLabel>
        <RadioCardField.Option
          isInteractive
          key={'day'}
          value={'day'}
          title={
            <div css={monthlyFrequencyOptionStyles}>
              <Text css={monthlyFrequencyTextStyles}>{t('On day')}</Text>
              <NthDayDropdown {...getFieldProps('monthlyFrequencyDay')} required={monthlyFrequencyType === 'day'} />
              <Text css={monthlyFrequencyTextStyles}>{t('of the month')}</Text>
            </div>
          }
        />
        <RadioCardField.Option
          isInteractive
          key={'weekday'}
          value={'weekday'}
          title={
            <div css={monthlyFrequencyOptionStyles}>
              <Text css={monthlyFrequencyTextStyles}>{t('On the')}</Text>
              <NthWeekStringDropdown
                {...getFieldProps('monthlyFrequencyWeek')}
                css={{ maxWidth: '150px' }}
                required={monthlyFrequencyType === 'weekday'}
              />
              <WeekdayDropdown
                {...getFieldProps('monthlyFrequencyWeekday')}
                css={{ maxWidth: '200px' }}
                required={monthlyFrequencyType === 'weekday'}
              />
              <Text css={monthlyFrequencyTextStyles}>{t('of the month')}</Text>
            </div>
          }
        />
      </RadioCardFieldRow>
    </AvailabilityFormRow>
  );
};

const monthlyFrequencyOptionStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
});

const monthlyFrequencyTextStyles = css({
  pointerEvents: 'none',
});
